import { NotSavedYetError } from "@/classes/errors";
import { ApiClient } from "./clients";
import { Comment, ListService, AllOptions, CommentReaction } from "@/types";
import { ticketUpdatedByMe } from "@/utils/socket";

export interface CommentListService extends ListService<Comment> {
  react?: (entity: Comment, emoji: string) => Promise<Comment>;
  togglePin?: (entity: Comment) => Promise<Comment>;
  unreact?: (reaction: any) => Promise<void>;
}

export const CommentsService: CommentListService = {
  async all(options?: AllOptions) {
    if (options?.ticket) {
      const comments = await ApiClient.get<{
        results: Comment[];
        count: number;
        next: string | null;
        previous: string | null;
      }>(`/api/v2/ticket/${options.ticket}/comments/`).then((data) => {
        if (!data?.results?.length) return [];
        return data.results.map((c) => ({ ...c, ticket: options.ticket }));
      });
      return comments;
    } else if (options?.instance) {
      const comments = await ApiClient.get<Comment[]>(
        `/api/v1/instance/${options.instance}/comments/`
      ).then((data) => {
        if (!data?.length || !Array.isArray(data)) return [];
        return data.map((c: any) => ({ ...c, instance: options.instance }));
      });
      return comments;
    }
    throw "No ticket or instance given";
  },
  async add(entity: Comment, request) {
    if (typeof entity.ticket === "number") {
      const comment = await ApiClient.post<Comment>(
        `/api/v1/ticket/${entity.ticket}/comment/`,
        {
          content: entity.content,
          mentioned_user_ids: entity.mentioned_user_ids,
          tagged_children: entity.tagged_children,
          parent: entity.parent,
        },
        false,
        request
      );
      if (!comment) {
        return;
      }
      return { ...comment, ticket: entity.ticket, uuid: entity.uuid };
    } else if (typeof entity.instance === "number") {
      const comment = await ApiClient.post<Comment>(
        `/api/v1/instance/${entity.instance}/comment/`,
        {
          content: entity.content,
          mentioned_user_ids: entity.mentioned_user_ids,
          tagged_children: entity.tagged_children,
          parent: entity.parent,
        },
        false,
        request
      );
      if (!comment) {
        return;
      }
      return { ...comment, instance: entity.instance, uuid: entity.uuid };
    }
    throw new NotSavedYetError("Ticket not saved yet");
  },
  async update(entity: Comment, columns: any, request) {
    if (typeof entity.ticket === "number") {
      const comment = await ApiClient.put<Comment>(
        `/api/v1/ticket/${entity.ticket}/comment/${entity.id}/`,
        columns,
        request
      );
      if (!comment) {
        return;
      }
      return { ...comment, ticket: entity.ticket };
    } else if (typeof entity.instance === "number") {
      const comment = await ApiClient.put<Comment>(
        `/api/v1/instance/${entity.instance}/comment/${entity.id}/`,
        columns,
        request
      );
      if (!comment) {
        return;
      }
      return { ...comment, instance: entity.instance };
    }
    throw new NotSavedYetError("Ticket not saved yet");
  },
  async remove(entity: Comment, request) {
    if (entity.ticket) ticketUpdatedByMe(+entity.ticket);
    await ApiClient.delete(`/api/v1/comment/${entity.id}/`, request);
  },
  async togglePin(entity: Comment) {
    if (entity.ticket) ticketUpdatedByMe(+entity.ticket);
    const comment = await ApiClient.put<Comment>(
      `/api/v1/comment/${entity.id}/togglePinned/`
    );
    if (!comment) {
      throw new Error("Comment not saved yet");
    }
    if (typeof entity.ticket === "number") {
      return { ...comment, ticket: entity.ticket };
    } else if (typeof entity.instance === "number") {
      return { ...comment, instance: entity.instance };
    }
    throw new NotSavedYetError("Ticket not saved yet");
  },
  async react(entity: Comment, emoji: string) {
    if (typeof entity.id === "number") {
      const reaction = await ApiClient.post<CommentReaction>(
        `/api/v1/comment/${entity.id}/reaction/`,
        { reaction: emoji }
      );
      if (!reaction) {
        throw new Error("Comment reaction not saved yet");
      }
      return {
        ...entity,
        reactions: [...(entity.reactions || []), reaction],
        ticket: entity.ticket,
      };
    }
    throw new NotSavedYetError("Comment not saved yet");
  },
  async unreact(entity: any): Promise<void> {
    await ApiClient.delete(`/api/v1/comment/reaction/${entity.id}/`);
  },
};
